/**
 * Document panel styles - LAYOUT ONLY.
 * Colors and hover states are defined in createBrandedTheme.ts.
 * Components should use MUI props (variant, color) instead of sx color overrides.
 */

import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { scrollbarStyles } from '../../../styles/sharedStyles';

export const documentStyles = {
  // Layout styles.
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    bgcolor: 'custom.backgroundAlt',
    p: 0.5,
    gap: 1,
  } as SxProps<Theme>,
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    bgcolor: 'custom.backgroundAlt',
    p: 1,
    pt: 0.5,
    gap: 1,
  } as SxProps<Theme>,
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    bgcolor: 'custom.backgroundAlt',
    p: 1.5,
  } as SxProps<Theme>,
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    p: 3,
    gap: 1.5,
  } as SxProps<Theme>,
  headerSpacer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 0,
  } as SxProps<Theme>,
  documentContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  } as SxProps<Theme>,

  // Skeleton styles.
  skeletonHeaderContainer: {
    display: 'flex',
    align: 'center',
    gap: 1,
    mb: 1,
  } as SxProps,
  skeletonContentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  } as SxProps,

  // Background generation state styles.
  generatingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 2,
    p: 3,
  } as SxProps,
  generatingTitle: {
    textAlign: 'center',
    fontWeight: 600,
  } as SxProps,
  generatingSubtitle: {
    textAlign: 'center',
    color: 'text.secondary',
  } as SxProps,

  // Empty state styles.
  emptyStateTitle: {
    textAlign: 'center',
    fontSize: '16px',
    color: 'text.disabled',
  } as SxProps,
  emptyStateDescription: {
    textAlign: 'center',
    fontSize: '14px',
    color: 'text.disabled',
  } as SxProps,

  // Generation error state styles.
  generationErrorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    p: 3,
    gap: 2,
    px: 1,
  } as SxProps,
  generationErrorIcon: {
    width: '64px',
    height: '64px',
    opacity: 0.7,
  } as SxProps,
  generationErrorText: {
    textAlign: 'center',
    fontSize: '14px',
    color: 'text.secondary',
    lineHeight: 1.5,
    fontWeight: 400,
    maxWidth: '350px',
  } as SxProps,
  generationErrorButton: {
    padding: '4px 16px',
    fontSize: '14px',
  } as SxProps,

  // Warning alert layout; colors come from MuiAlert theme overrides.
  warningAlert: {
    fontSize: '8px',
    padding: '6px 12px',
    transition: 'opacity 0.3s ease-out',
    opacity: 1,
    my: '10px',
    '& .MuiAlert-message': {
      fontSize: '8px',
      padding: 0,
      width: '100%',
    },
    '& .MuiAlert-icon': {
      fontSize: '20px',
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
    },
    '& .MuiAlert-action': {
      '& .MuiIconButton-root': {
        padding: '2px',
        '& .MuiSvgIcon-root': {
          fontSize: '20px',
        },
      },
    },
  } as SxProps<Theme>,
  warningContainer: {
    width: '100%',
  } as SxProps<Theme>,
  warningTitle: {
    fontWeight: 'bold',
    fontSize: '12px',
    marginBottom: '3px',
    letterSpacing: '0.3px',
  } as SxProps<Theme>,
  warningCheckbox: {
    color: '#e65100',
    '&.Mui-checked': {
      color: '#e65100',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '16px',
    },
  } as SxProps<Theme>,
  warningCheckboxLabel: {
    fontSize: '10px',
    color: '#e65100',
    '& .MuiFormControlLabel-label': {
      fontSize: '10px',
      color: '#e65100',
    },
    '& .MuiCheckbox-root': {
      padding: '2px 6px 2px 0',
    },
    margin: 0,
  } as SxProps<Theme>,

  // Session captured layout styles.
  sessionCapturedContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    py: 0.5,
    px: 1.5,
    mb: 0.75,
  } as SxProps<Theme>,
  sessionCapturedTextContainer: {
    flex: 1,
  } as SxProps<Theme>,
  sessionCapturedTitle: {
    fontWeight: 600,
    fontSize: '16px',
    mb: 0.125,
    lineHeight: 1.2,
  } as SxProps<Theme>,
  sessionCapturedDescription: {
    fontSize: '14px',
    lineHeight: 1.3,
  } as SxProps<Theme>,
  sessionCapturedButtonsContainer: {
    display: 'flex',
    gap: 0.75,
    alignItems: 'center',
  } as SxProps<Theme>,
  sessionCapturedButtonText: {
    fontSize: '14px',
    fontWeight: 500,
  } as SxProps<Theme>,
  saveButtonIcon: (theme: Theme) => ({
    marginRight: '10px',
    color: theme.palette.text.primary,
    '& .MuiSvgIcon-root': { fontSize: '32px' },
    '&&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      color: theme.palette.primary.main,
    },
  }),
  saveButtonMenu: {
    maxWidth: 'none',
    zIndex: 1000001,
    border: 1,
    borderStyle: 'solid',
    borderColor: 'divider',
    borderRadius: 1,
    boxShadow: 'custom.menuShadow',
  } as SxProps<Theme>,
  saveButtonMenuItem: {
    padding: '8px 16px',
  } as SxProps<Theme>,

  // Document content layout; use Paper variant="panel" for colors.
  contentPaper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    overflow: 'hidden',
    position: 'relative',
  } as SxProps<Theme>,
  contentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    p: 0.25,
    minHeight: '26px',
    borderBottom: 1,
    borderColor: 'divider',
    alignItems: 'center',
  } as SxProps<Theme>,
  contentHeaderContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 1,
  } as SxProps<Theme>,
  contentTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  } as SxProps<Theme>,
  contentTitle: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.2,
  } as SxProps<Theme>,
  contentTimestampContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as SxProps<Theme>,
  contentTimestamp: {
    fontSize: '14px',
    color: 'text.secondary',
  } as SxProps<Theme>,
  contentSavingStatus: {
    fontSize: '14px',
    color: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  } as SxProps<Theme>,
  contentSavedStatus: {
    fontSize: '14px',
    color: 'text.secondary',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  } as SxProps<Theme>,
  contentSaveIcon: {
    fontSize: '14px',
    color: 'primary.main',
  } as SxProps<Theme>,
  contentSavedIcon: {
    fontSize: '14px',
    color: '#22c55e',
  } as SxProps<Theme>,
  contentBody: {
    flex: 1,
    px: 1,
    pb: 1,
  } as SxProps<Theme>,
  contentBodyScrollable: {
    flex: 1,
    px: 1,
    pb: 1,
    overflowY: 'auto',
    ...scrollbarStyles,
  } as SxProps<Theme>,

  // Document field layout; use MUI color props.
  fieldContainer: {
    width: '100%',
  } as SxProps,
  fieldLabelContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 0.5,
  } as SxProps,
  fieldLabel: {
    fontWeight: 600,
    textTransform: 'capitalize',
    fontSize: '14px',
    color: 'text.primary',
    mb: 0.5,
    letterSpacing: '0px',
    padding: 0,
    display: 'block',
    width: '100%',
    margin: 0,
    boxSizing: 'border-box',
  } as SxProps,
  fieldLabelPrimary: {
    fontWeight: 600,
    textTransform: 'capitalize',
    fontSize: '14px',
    mb: 0.5,
    letterSpacing: '0px',
    padding: 0,
    display: 'block',
    width: '100%',
    margin: 0,
    boxSizing: 'border-box',
    color: 'text.primary',
  } as SxProps,
  fieldTextField: {
    mt: 1,
    '& .MuiOutlinedInput-root': {
      lineHeight: 1.4,
      minHeight: 'auto',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: '1px solid',
        borderWidth: '1px',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0.5px 0px',
    },
  } as SxProps,
  fieldCheckbox: {} as SxProps,

  // Document field style utilities.
  createDocumentFieldStyles: (
    scaleFontSize: (size: string) => string
  ): SxProps<Theme> => ({
    mt: 0.25,
    mb: 1,
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      fontSize: scaleFontSize('14px'),
      lineHeight: 1.4,
      minHeight: 'auto',
      borderRadius: '2px',
      position: 'relative',
      '& fieldset': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'divider',
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'text.disabled',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '2px',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '4px',
        right: '4px',
        width: '12px',
        height: '12px',
        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M1 11L11 1' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M6 11L11 6' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        pointerEvents: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 18px 6px 6px',
    },
  }),

  createStringContentFieldStyles: (
    scaleFontSize: (size: string) => string
  ): SxProps<Theme> => ({
    mt: 0.25,
    mb: 1,
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      fontSize: scaleFontSize('10px'),
      lineHeight: 1.4,
      borderRadius: '4px',
      position: 'relative',
      '& fieldset': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'divider',
        borderRadius: '4px',
      },
      '&:hover fieldset': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'text.disabled',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '2px',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '4px',
        right: '4px',
        width: '8px',
        height: '8px',
        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M1 11L11 1' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M6 11L11 6' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        pointerEvents: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 18px 6px 6px',
    },
  }),
};
